import { createContext, ReactNode, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { initializeApp } from 'firebase/app';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import {
  getAuth,
  connectAuthEmulator,
  signOut,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  deleteUser,
} from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  DocumentData,
  initializeFirestore,
  connectFirestoreEmulator,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
// @types
import { ActionMap, AuthState, AuthUser, FirebaseContextType } from '../@types/auth';
//
import { FIREBASE_API } from '../config/config';
import {
  getStorage,
  ref,
  uploadBytesResumable,
  deleteObject,
  getDownloadURL,
} from 'firebase/storage';
import {
  CustomClaims,
  InfoPublicInviter,
  Invitation,
  InviteDataClientArgs,
  InviteDataClientPublicArgs,
  ReqNewVerifLinkArgs,
  SignUpArgs,
  UpdateUserInviteArgs,
  UpdateUserSignUpArgs,
  UserCtx,
  UserDb,
} from 'src/guards/admin/types';
import { getPermissionsFromPcodes } from 'src/guards/admin/utility';
import { isAgentData, isStaffData, isPromoterData } from 'src/guards/admin/guards';
import { LEGACY_BACKEND_MESSAGES, LegacyBackendMessageKey } from 'src/old_locales';
/* import { useSnackbar } from 'notistack'; */
// ----------------------------------------------------------------------
var firebasecredentials = FIREBASE_API.prod;
console.log('process.env.REACT_APP_FIREBASE_PROJECT', process.env.REACT_APP_FIREBASE_PROJECT);
if (process.env.REACT_APP_FIREBASE_PROJECT === 'instadrinkuat') {
  firebasecredentials = FIREBASE_API.hom;
} else if (process.env.REACT_APP_FIREBASE_PROJECT === 'instadrink-47f0f') {
  firebasecredentials = FIREBASE_API.dev;
} else if (process.env.REACT_APP_FIREBASE_PROJECT === 'instadrink-eecf1') {
  firebasecredentials = FIREBASE_API.prod;
}

//console.log('firebasecredentials', firebasecredentials);

const firebaseApp = initializeApp(firebasecredentials);
export const AUTH = getAuth(firebaseApp);
export const DB = initializeFirestore(firebaseApp, { ignoreUndefinedProperties: true });
//export const DB = getFirestore(firebaseApp);
const STORAGE = getStorage(firebaseApp);
const FUNCTIONS_REGION = process.env.REACT_APP_FUNCTIONS_REGION || 'europe-west1';
export const FUNCTIONS = getFunctions(firebaseApp, FUNCTIONS_REGION);

// Connect to local emulators when requested via env
if (process.env.REACT_APP_USE_EMULATORS === 'true') {
  const host = process.env.REACT_APP_EMULATOR_HOST || 'localhost';
  const authPort = Number(process.env.REACT_APP_AUTH_EMULATOR_PORT) || 9099;
  const fsPort = Number(process.env.REACT_APP_FIRESTORE_EMULATOR_PORT) || 5003;
  const fnPort = Number(process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT) || 5001;
  try {
    connectAuthEmulator(AUTH, `http://${host}:${authPort}`, { disableWarnings: true });
  } catch (_) {
    // ignore if called twice during HMR
  }
  try {
    connectFirestoreEmulator(DB, host, fsPort);
  } catch (_) {
    // ignore if called twice during HMR
  }
  try {
    connectFunctionsEmulator(FUNCTIONS, host, fnPort);
  } catch (_) {
    // ignore if called twice during HMR
  }
}
const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};
enum Types {
  Initial = 'INITIALISE',
}

type FirebaseAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
};
type FirebaseActions = ActionMap<FirebaseAuthPayload>[keyof ActionMap<FirebaseAuthPayload>];
const reducer = (state: AuthState, action: FirebaseActions) => {
  if (action.type === 'INITIALISE') {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  }
  return state;
};
const AuthContext = createContext<FirebaseContextType | null>(null);
// ----------------------------------------------------------------------
type AuthProviderProps = {
  children: ReactNode;
};
export interface UsersData {
  allIds: string[];
  userIds: string[];
  agentUserIds: string[];
  agentsIds: { [key: string]: boolean };
  claimsData: CustomClaims[];
  sortedUsers: CustomClaims[];
  sortedAgents: CustomClaims[];
}
function AuthProvider({ children }: AuthProviderProps) {
  const { t, i18n } = useTranslation();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [profile, setProfile] = useState<UserDb | undefined>();
  const [claims, setClaims] = useState<CustomClaims>();
  const [isLoading, setIsLoading] = useState(true);

  const isBackendMessageKey = (value: string): value is LegacyBackendMessageKey =>
    Object.prototype.hasOwnProperty.call(LEGACY_BACKEND_MESSAGES, value);

  const normalizeFunctionsCode = (code?: string): string | undefined => {
    if (!code || typeof code !== 'string') return undefined;
    const normalized = code.replace(/^functions\//, '').trim();
    return normalized || undefined;
  };

  const resolveBackendMessage = (message?: string): { display: string; key?: string } => {
    if (!message || typeof message !== 'string') {
      return { display: '' };
    }
    const trimmed = message.trim();
    if (!trimmed) {
      return { display: '' };
    }

    if (i18n.exists(trimmed)) {
      const translated = t(trimmed, { defaultValue: trimmed });
      if (translated) {
        return { display: translated, key: trimmed };
      }
    }

    if (isBackendMessageKey(trimmed)) {
      return { display: LEGACY_BACKEND_MESSAGES[trimmed], key: trimmed };
    }

    return { display: trimmed };
  };

  const formatCallableError = (error: any): Error => {
    let normalizedCode = normalizeFunctionsCode(error?.code);
    let rawMessage = typeof error?.message === 'string' ? error.message.trim() : undefined;

    if (rawMessage && rawMessage.includes(':')) {
      const [maybeCode, ...rest] = rawMessage.split(':');
      const candidateCode = normalizeFunctionsCode(maybeCode);
      if (!normalizedCode && candidateCode) {
        normalizedCode = candidateCode;
      }
      const joined = rest.join(':').trim();
      if (joined) {
        rawMessage = joined;
      }
    }

    const { display, key } = resolveBackendMessage(rawMessage);
    const fallback = display || 'Something went wrong.';
    const formattedError: any = new Error(fallback);
    if (normalizedCode) {
      formattedError.code = normalizedCode;
    }
    else if (typeof error?.code === 'string' && error.code.trim()) {
      formattedError.code = normalizeFunctionsCode(error.code) ?? error.code;
    }
    if (key) {
      formattedError.messageKey = key;
    }
    formattedError.rawMessage = rawMessage;
    return formattedError;
  };
  const onChangeUser = (user: DocumentData) => {
    const UserDb = user as UserDb;
    setProfile(UserDb);
  };
  // console.log('isAuthenticated in contexte', state.isAuthenticated);

  useEffect(
    () =>
      onAuthStateChanged(AUTH, async (user) => {
        //console.log('user', user);
        if (user) {
          const idTokenResult = await user.getIdTokenResult();
          //const api = parseFloat(idTokenResult?.claims?.api as string ?? '0') || 0;
          const claims = idTokenResult.claims as unknown as CustomClaims;

          const api = claims.api || 0;
          let custClaim = idTokenResult.claims as unknown as CustomClaims;
          setClaims(custClaim);
          const userRef = doc(DB, 'users', user.uid);
          const docSnap = await getDoc(userRef);
          if (docSnap.exists()) {
            const userData = docSnap.data() as UserDb;
            setProfile(userData);
          }

          dispatch({
            type: Types.Initial,
            payload: { isAuthenticated: true, user },
          });
        } else {
          console.log('No user');
          dispatch({
            type: Types.Initial,
            payload: { isAuthenticated: false, user: null },
          });
        }
        setIsLoading(false);
      }),
    [dispatch, setProfile, setIsLoading]
  );
  const getClaims = async ({ orgId }: { orgId: string }): Promise<UsersData> => {
    try {
      if (!orgId) throw new Error('Org Id is required.');
      console.log(`Loading claims for ${orgId} users...`);
      const getClaims = httpsCallable(FUNCTIONS, 'getCusomClaimsData');
      const result = (await getClaims({ orgId })) as { data: UsersData };
      console.log(`Successfully loaded claims for ${result.data.allIds.length} users`);
      return result.data;
    } catch (error) {
      console.error(`Error loading claims for ${orgId} users : ${error}`);
      throw formatCallableError(error);
    }
  };
  const getClaimsAmb = async (): Promise<UsersData> => {
    try {
      console.log(`Loading claims for ambassador invites...`);
      const getClaimsAmb = httpsCallable(FUNCTIONS, 'getCusomClaimsDataAmb');
      const result = (await getClaimsAmb()) as { data: UsersData };
      console.log(result.data);
      console.log(`Successfully loaded claims for ${result.data.allIds.length} users`);
      return result.data;
    } catch (error) {
      console.error(`Error loading claims for ambassador invites...`);
      throw formatCallableError(error);
    }
  };
  // const login = (email: string, password: string) =>
  //   signInWithEmailAndPassword(AUTH, email, password);

  const login = async (email: string, password: string) => {
    console.log('Starting login process...', { email });
    try {
      console.log('Attempting sign in...');
      const userCredential = await signInWithEmailAndPassword(AUTH, email, password);
      const currentUser = userCredential.user;

      console.log('Sign in result:', {
        success: !!userCredential,
        userId: currentUser?.uid,
        emailVerified: currentUser?.emailVerified,
      });

      if (!currentUser) {
        throw new Error('No user after login');
      }

      console.log('Updating last login time...');
      const userdocRef = doc(DB, 'users', currentUser.uid);
      await updateDoc(userdocRef, {
        lastLoggedIn: Timestamp.fromDate(new Date()),
      });

      console.log('Waiting for auth state to stabilize...');
      await new Promise((resolve) => setTimeout(resolve, 1000));

      console.log('Login complete. Current auth state:', {
        currentUser: AUTH.currentUser?.uid,
        emailVerified: AUTH.currentUser?.emailVerified,
        isAnonymous: AUTH.currentUser?.isAnonymous,
      });

      return userCredential;
    } catch (error) {
      console.error('Login error:', {
        error,
        code: error.code,
        message: error.message,
        authState: {
          currentUser: AUTH.currentUser?.uid,
          emailVerified: AUTH.currentUser?.emailVerified,
        },
      });
      throw error;
    }
  };
  const resetPassword = (email: string) => sendPasswordResetEmail(AUTH, email);
  const register = (email: string, password: string, firstName: string, lastName: string) =>
    createUserWithEmailAndPassword(AUTH, email, password).then(async (res) => {
      const userRef = doc(collection(DB, 'users'), res.user?.uid);
      await setDoc(userRef, {
        uid: res.user?.uid,
        email,
        displayName: `${firstName} ${lastName}`,
        role: 'Owner',
        firstName: firstName,
        lastName: lastName,
        // Don't set isNewUser - it's only for backward compatibility with old users
        // New users should have undefined/unset isNewUser and use progressive onboarding
      });
    });
  /*  const createUser = async (newUser: NewUser) =>
   {
     try
     {
       // Call the Cloud Function to create a new user with custom claims and add it to Firestore
       const createUser = httpsCallable(FUNCTIONS, 'createUser');
       const result = await createUser({ newUser });
       console.log(result);
       if (result.data) console.log('Successfully created new user:', newUser.email);
       else console.log('Failed to create new user:', newUser.email);
     } catch (error)
     {
       console.error('Error creating new user:', error);
       throw new Error(error);
     }
   }; */
  /*   const createOwner = async (newOwner: NewSignUp) =>
    {
      try
      {
        if (!newOwner.password)
        {
          throw new Error('Password is required.');
        }
        const createOwner = httpsCallable(FUNCTIONS, 'createOwner');
        const result = await createOwner({ newOwner });
        if (result?.data === true)
        {
          console.log('Successfully created new owner:', newOwner.email);
          signInWithEmailAndPassword(AUTH, newOwner.email, newOwner.password);
        } else console.log('Failed to create new owner:', newOwner.email);
      } catch (error)
      {
        console.error('Error creating new owner:', error);
        throw new Error(error);
      }
    }; */

  const signupUserCallable = async (signUpArgs: SignUpArgs): Promise<boolean> => {
    const { firstName, lastName, email, division, role, lang } = signUpArgs;
    const language = lang || 'en';
    if (!firstName || !lastName || !division || !role || !language)
      throw new Error('Missing fields');
    try {
      const signupUser = httpsCallable(FUNCTIONS, 'signupUser');
      const result = await signupUser(signUpArgs);
      if (result?.data === true) {
        return true;
        // signInWithEmailAndPassword(AUTH, email, password);
      } else console.log('Failed to create new user:', email);
    } catch (error) {
      throw formatCallableError(error);
    }
    return false;
  };

  const requestInvitationCallable = async (
    inviteDataClientArgs: InviteDataClientArgs
  ): Promise<boolean> => {
    const { email, firstName, lastName, invitedRole, invitedDivision, lang } = inviteDataClientArgs;
    const language = lang || 'en';
    if (!firstName || !lastName || !invitedRole || !invitedDivision || !language)
      throw new Error('Missing fields');
    try {
      const requestInvitation = httpsCallable(FUNCTIONS, 'requestInvitation');
      const result = await requestInvitation(inviteDataClientArgs);
      if (result?.data === true) {
        return true;
        // signInWithEmailAndPassword(AUTH, email, password);
      } else console.log('Failed to request invite user:', email);
    } catch (error) {
      throw formatCallableError(error);
    }
    return false;
  };

  const acceptInviteUserCallable = async (tokenData: {
    token: string;
    firstName: string;
    lastName: string;
    lang: string;
    password: string;
  }): Promise<boolean> => {
    try {
      const { token, firstName, lastName, password } = tokenData;
      if (!token || !firstName || !lastName || !password) {
        throw new Error('Missing required fields');
      }

      console.log('Accepting invitation with token data...');
      const acceptInviteUser = httpsCallable(FUNCTIONS, 'acceptInviteUser');
      const result = await acceptInviteUser(tokenData);

      if (result?.data === true) {
        return true;
      } else {
        console.log('Failed to accept invitation');
        return false;
      }
    } catch (error) {
      throw formatCallableError(error);
    }
  };

  const updateInvitedUserCallable = async (
    updateUserInviteArgs: UpdateUserInviteArgs
  ): Promise<boolean> => {
    const { firstName, lastName, password, lang } = updateUserInviteArgs;
    const language = lang || 'en';
    if (!firstName || !lastName || !password || !language) throw new Error('Missing fields');
    try {
      const updateInvitedUser = httpsCallable(FUNCTIONS, 'updateInvitedUser');
      const result = await updateInvitedUser(updateUserInviteArgs);
      if (result?.data === true) {
        return true;
        // signInWithEmailAndPassword(AUTH, email, password);
      } else console.log('Failed to request invite user:', firstName);
    } catch (error) {
      throw formatCallableError(error);
    }
    return false;
  };

  const rejectInviteUserCallable = async (token: string): Promise<boolean> => {
    if (!token) throw new Error('Token is required');
    try {
      console.log('Rejecting invitation with token...');
      const rejectInviteUser = httpsCallable(FUNCTIONS, 'rejectInviteUser');
      const result = await rejectInviteUser({ token });

      if (result?.data === true) {
        return true;
      } else {
        console.log('Failed to reject invitation');
        return false;
      }
    } catch (error) {
      throw formatCallableError(error);
    }
  };

  const cancelInviteUserCallable = async (invitationId: string): Promise<boolean> => {
    if (!invitationId) throw new Error('Missing fields');
    try {
      const cancelInviteUser = httpsCallable(FUNCTIONS, 'cancelInviteUser');
      const result = await cancelInviteUser({ invitationId });
      if (result?.data === true) {
        return true;
        // signInWithEmailAndPassword(AUTH, email, password);
      } else console.log('Failed to cancel invite user with invitation:', invitationId);
    } catch (error) {
      throw formatCallableError(error);
    }
    return false;
  };

  const finishOnboardingCallable = async (newUserId: string): Promise<boolean> => {
    if (!newUserId) throw new Error('Missing fields');
    try {
      const finishOnboarding = httpsCallable(FUNCTIONS, 'finishOnboarding');
      const result = await finishOnboarding(newUserId);
      if (result?.data === true) {
        return true;
        // signInWithEmailAndPassword(AUTH, email, password);
      } else console.log('Failed to finish onboarding:', newUserId);
    } catch (error) {
      throw formatCallableError(error);
    }
    return false;
  };

  const updateSignupUserCallable = async (
    updateUserSignUpArgs: UpdateUserSignUpArgs
  ): Promise<boolean> => {
    try {
      console.log('Verifying account...');
      const updateUser = httpsCallable(FUNCTIONS, 'updateSignupUser');
      const result = await updateUser(updateUserSignUpArgs);
      console.log(result);
      console.log(result?.data);
      if (result?.data === true) {
        console.log('Successfully verified email:');
        return true;
      } else console.log('Failed to verify email:');
    } catch (error) {
      console.error('Error verify email:', error);
      throw formatCallableError(error);
    }
    return false;
  };

  const requestNewVerificationLinkCallable = async (
    reqNewLinkArgs: ReqNewVerifLinkArgs
  ): Promise<boolean> => {
    try {
      const { email } = reqNewLinkArgs;
      console.log('Requesting new verification link...');
      const requestNewVerificationLink = httpsCallable(FUNCTIONS, 'requestNewVerificationLink');
      const result = await requestNewVerificationLink(email);
      console.log(result);
      console.log(result?.data);
      if (result?.data === true) {
        console.log('Successfully requested new verification link:');
        return true;
      } else console.log('Failed to request new verification link:');
    } catch (error) {
      console.error('Error request new verification link:', error);
      throw formatCallableError(error);
    }
    return false;
  };

  const requestNewInviteLinkCallable = async (invitationId: string): Promise<boolean> => {
    try {
      console.log('Requesting new verification link...');
      const requestNewInviteLink = httpsCallable(FUNCTIONS, 'requestNewInviteLink');
      const result = await requestNewInviteLink(invitationId);
      console.log(result);
      console.log(result?.data);
      if (result?.data === true) {
        console.log('Successfully requested new verification link:');
        return true;
      } else console.log('Failed to request new verification link:');
    } catch (error) {
      console.error('Error request new verification link:', error);
      throw formatCallableError(error);
    }
    return false;
  };

  const requestNewSignupLinkCallable = async (email: string): Promise<boolean> => {
    try {
      console.log('Requesting new verification link...');
      const requestNewSignupLink = httpsCallable(FUNCTIONS, 'requestNewSignupLink');
      const result = await requestNewSignupLink(email);
      console.log(result);
      console.log(result?.data);
      if (result?.data === true) {
        console.log('Successfully requested new verification link:');
        return true;
      } else console.log('Failed to request new verification link:');
    } catch (error) {
      console.error('Error request new verification link:', error);
      throw formatCallableError(error);
    }
    return false;
  };

  const validateInviteToken = async (token: string): Promise<Invitation> => {
    try {
      console.log('Validating invitation token...');
      const validateToken = httpsCallable(FUNCTIONS, 'validateInviteToken');
      const result = await validateToken({ token });

      if (!result?.data) {
        console.error('Invalid token validation response');
        throw new Error('Invalid or expired invitation token');
      }

      console.log('Successfully validated token:', result.data);
      return result.data as Invitation;
    } catch (error) {
      console.error('Error validating invitation token:', error);
      throw formatCallableError(error);
    }
  };

  const validateInviter = async (inviterId: string): Promise<InfoPublicInviter> => {
    try {
      console.log('Validating inviter ID...');
      const validateInviterFn = httpsCallable(FUNCTIONS, 'validateInviter');
      const result = await validateInviterFn({ inviterId });

      if (!result?.data) {
        console.error('Invalid inviter validation response');
        throw new Error('Invalid inviter ID');
      }

      console.log('Successfully validated inviter:', result.data);
      return result.data as InfoPublicInviter;
    } catch (error: any) {
      console.error('Error validating inviter:', error);
      throw formatCallableError(error);
    }
  };

  const requestPublicInvitation = async (
    inviteDataClientPublicArgs: InviteDataClientPublicArgs
  ): Promise<boolean> => {
    const { email, firstName, lastName, invitedRole, invitedDivision, inviterId, lang } =
      inviteDataClientPublicArgs;
    const language = lang || 'en';
    if (!firstName || !lastName || !invitedRole || !invitedDivision || !language || !inviterId)
      throw new Error('Missing fields');
    try {
      const requestPubInvitation = httpsCallable(FUNCTIONS, 'requestPublicInvitation');
      const result = await requestPubInvitation(inviteDataClientPublicArgs);
      if (result?.data === true) {
        return true;
      } else console.log('Failed to request public invitation for user:', email);
    } catch (error: any) {
      throw formatCallableError(error);
    }
    return false;
  };

  const logout = () => signOut(AUTH);

  const unregistration = async () => {
    try {
      await deleteUser(AUTH.currentUser as any);
      console.log('AUTH', AUTH.currentUser);
    } catch (error) {
      console.log('error deleting user: ', error);
    }
  };
  const permissions = getPermissionsFromPcodes(claims?.pcodes || []);

  let orgId: string | undefined,
    ownerId: string | undefined,
    orgType: string | undefined;

  if (isStaffData(claims)) {
    ({ orgId, ownerId, orgType } = claims);
  }

  let invitationId: string | null | undefined,
    inviterId: string | null | undefined,
    isInvite: boolean | undefined = undefined;

  if (isAgentData(claims) || isPromoterData(claims)) {
    ({ invitationId, inviterId, isInvite } = claims);
  }

  let ancestors: string[] | undefined,
    mainAncestorId: string | null | undefined,
    level: number | undefined,
    orgs: string[] | null | undefined;

  if (isPromoterData(claims)) {
    ({ ancestors, mainAncestorId, level, orgs } = claims);
  }

  let user: UserCtx = {
    dataType: 'Ctx',
    api: claims?.api || 0,
    // InfoUserData
    uid: claims?.user_id || state?.user?.uid || profile?.uid || '',
    firstName: claims?.firstName || profile?.firstName || 'firstName',
    lastName: claims?.lastName || profile?.lastName || 'lastName',
    displayName: claims?.displayName || profile?.displayName || '',
    email: claims?.email || state?.user?.email,
    lang: claims?.lang || 'fr',
    // DivisionUserData
    type: claims?.type,
    code: claims?.code,
    division: claims?.division,
    role: profile?.role,
    /// DivisionUserData staff
    orgId: profile?.orgId || '',
    ownerId,
    orgType,
    /// DivisionUserData invited promoters or agent
    isInvite,
    invitationId,
    inviterId: inviterId,
    /// DivisionUserData promoters
    ancestors: profile?.ancestors || [],
    mainAncestorId,
    level,
    orgs,
    // ExtraUserData
    photoURL: profile?.photoURL || '',
    phoneNumber: profile?.phoneNumber || '',
    address: profile?.address || '',
    city: profile?.city || '',
    state: profile?.state || '',
    zipCode: profile?.zipCode || '',
    country: profile?.country || '',
    about: profile?.about || '',
    creationTime: profile?.creationTime || '',
    // OnboardingUserData
    onBoardingSteps: claims?.onBoardingSteps || [],
    // Keep isNewUser undefined if not set - don't default to false!
    // undefined = use progressive onboarding, false = old user skip onboarding
    isNewUser: profile?.isNewUser,
    // AccessUserData
    access: claims?.access || { access_insta: false, access_insta_pro: false },
    pcodes: claims?.pcodes || [],
    // AccountUserData
    email_verified: claims?.email_verified || false,
    isPaymentVerified: claims?.isPaymentVerified || false,
    accountDisabled: claims?.accountDisabled || false,
    // PermissionUserData
    permissions,
    // MoreUserData
    stripeConnect: profile?.stripeConnect,
    referredevents: profile?.referredevents || [],
    bypassStripeConnect: profile?.bypassStripeConnect,
    refundEnabled: profile?.refundEnabled,
    notificationsOn: profile?.notificationsOn,
    // DefaultClaims
    user_id: claims?.user_id || state?.user?.uid || profile?.uid || '',
  } as UserCtx;

  //console.log(user);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'firebase',
        user,
        profile,
        isLoading,
        login,
        register,
        logout,
        resetPassword,
        getClaims,
        getClaimsAmb,
        onChangeUser,
        signupUserCallable,
        updateSignupUserCallable,
        requestInvitationCallable,
        acceptInviteUserCallable,
        requestNewVerificationLinkCallable,
        requestNewInviteLinkCallable,
        requestNewSignupLinkCallable,
        updateInvitedUserCallable,
        rejectInviteUserCallable,
        cancelInviteUserCallable,
        finishOnboardingCallable,
        isSignInWithEmailLink,
        signInWithEmailLink,
        validateInviteToken,
        validateInviter,
        requestPublicInvitation,
        unregistration,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
/* function uploadFileFirebase(file: File, path: string) {
  const storageRef = ref(STORAGE, path);
  return uploadBytesResumable(storageRef, file);
} */
const uploadFileFirebase = async (file: File, path: string) => {
  const filename = new Date().getTime() + file.name;
  const storageRef = ref(STORAGE, path + filename);
  const uploadTask = uploadBytesResumable(storageRef, file);
  await uploadTask;
  const fileUrl = await getDownloadURL(uploadTask.snapshot.ref);
  return fileUrl;
};
const deleteFileFirebase = async (path: string) => {
  const storageRef = ref(STORAGE, path);
  return deleteObject(storageRef);
};
export { AuthContext, AuthProvider, uploadFileFirebase, deleteFileFirebase };
